import React from "react";
import { Box, Typography, Avatar } from "@mui/material";
import { leerBaul } from "../../../services/referralBaul";
import medicalFunnelImg from "../../../assets/images/referidos/medical-funnel.webp";

// ─── Colores Pastel (contraste sobre fondo blanco) ───────────────────────────

const PASTEL_COLORS = [
  "#FFB3BA", "#BAFFC9", "#BAE1FF", "#E8BAFF", "#B3FFEC", "#C9B3FF", "#FFB3E6",
];

const getPastelColor = (letter) => {
  if (!letter) return PASTEL_COLORS[0];
  return PASTEL_COLORS[letter.toUpperCase().charCodeAt(0) % PASTEL_COLORS.length];
};

const extractFirstName = (msg) => {
  if (!msg) return "";
  const match = msg.match(/^([^\s]+)/);
  return match ? match[1] : "";
};

// ─── ReferralBanner ──────────────────────────────────────────────────────────
// Props:
//   success: bool — si true usa el mensaje de éxito del baúl

const ReferralBanner = ({ success = false }) => {
  // ── Snapshot congelado al montar ──────────────────────────────────────
  // Se lee UNA sola vez (useState initializer) para que el banner muestre
  // la misma info durante todo el wizard de registro.
  // En la pantalla de éxito, useEffect borra el localStorage DESPUÉS del
  // primer render, pero el snapshot en React state sigue intacto → el
  // usuario ve el banner final y recién al navegar fuera se descarta todo.
  const [estadoBaul] = React.useState(() => {
    const baul = leerBaul();
    return {
      foto:        baul?.foto        || "",
      msg:         success
        ? (baul?.mensajeExito       || "")
        : (baul?.mensajeInvitacion  || ""),
      storedColor: baul?.colorPastel || null,
      esDuoEmprende: baul?.esDuoEmprende === true,
    };
  });

  const { foto, msg, storedColor, esDuoEmprende } = estadoBaul;

  if (!msg) return null;

  const primerNombre = extractFirstName(msg);
  const primeraLetra = primerNombre ? primerNombre[0].toUpperCase() : "?";
  const pastelColor  = storedColor || getPastelColor(primeraLetra);

  const cardSx = {
    width: "100%",
    p: { xs: "16px", md: "20px" },
    borderRadius: { xs: "16px", md: "20px" },
    background: "#ffffff",
    boxShadow: "0 2px 4px rgba(0,0,0,0.03), 0 8px 16px rgba(0,0,0,0.03), 0 12px 24px rgba(0,0,0,0.05)",
    border: "1px solid #eaeaea",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: { xs: "12px", md: "16px" },
  };

  return (
    <Box sx={cardSx}>
      <Box sx={{ position: "relative", flexShrink: 0 }}>
        {esDuoEmprende ? (
          // Alianza DuoEmprende: miniatura rectangular de Medical Funnel en vez de la letra
          <Box
            component="img"
            src={medicalFunnelImg}
            alt="Medical Funnel"
            sx={{ height: { xs: 40, md: 44 }, width: "auto", borderRadius: "8px", display: "block" }}
          />
        ) : foto ? (
          <Avatar
            src={foto}
            alt={primerNombre}
            sx={{ width: { xs: 40, md: 44 }, height: { xs: 40, md: 44 }, borderRadius: "8px" }}
            variant="rounded"
          />
        ) : (
          <Box
            sx={{
              width: { xs: 40, md: 44 },
              height: { xs: 40, md: 44 },
              borderRadius: "8px",
              backgroundColor: pastelColor,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography sx={{ fontFamily: "Poppins-SemiBold", fontSize: { xs: "18px", md: "20px" }, color: "#1c1c1c", lineHeight: 1 }}>
              {primeraLetra}
            </Typography>
          </Box>
        )}
        <Box
          sx={{
            position: "absolute",
            bottom: "-6px",
            right: "-8px",
            fontSize: "14px",
            lineHeight: 1,
            userSelect: "none",
            animation: "giftFloat 2s ease-in-out infinite",
            "@keyframes giftFloat": {
              "0%":   { transform: "translateY(0px)" },
              "50%":  { transform: "translateY(-4px)" },
              "100%": { transform: "translateY(0px)" },
            },
          }}
        >
          🎁
        </Box>
      </Box>

      <Typography sx={{ fontFamily: "Quicksand-Regular", fontSize: { xs: "13px", md: "14px" }, color: "#6b7280", lineHeight: 1.5 }}>
        {msg}
      </Typography>
    </Box>
  );
};

export default ReferralBanner;
